import type { Component } from 'vue'
import ImageIcon from '@clevver/assets/icons/image.svg'
import DocIcon from '@clevver/assets/icons/doc.svg'
import CodeIcon from '@clevver/assets/icons/code.svg'
import VideoIcon from '@clevver/assets/icons/video.svg'
import AudioIcon from '@clevver/assets/icons/audio.svg'
import DataIcon from '@clevver/assets/icons/data.svg'

export type Kind = 'image' | 'doc' | 'code' | 'video' | 'audio' | 'data'

// Kind -> monochrome icon component (.svg compiled by vite-plugin-svg-component).
export const KIND_ICON: Record<Kind, Component> = {
  image: ImageIcon, doc: DocIcon, code: CodeIcon,
  video: VideoIcon, audio: AudioIcon, data: DataIcon,
}

// content_type -> kind bucket (used for the colored placeholder tile/icon).
export function kindFor(contentType: string): Kind {
  const ct = (contentType || '').toLowerCase()
  if (ct.startsWith('image/')) return 'image'
  if (ct.startsWith('video/')) return 'video'
  if (ct.startsWith('audio/')) return 'audio'
  if (ct === 'text/csv' || ct.includes('spreadsheet') || ct.includes('excel')) return 'data'
  if (ct === 'application/pdf' || ct === 'text/markdown' || ct.includes('msword') || ct.includes('officedocument.word')) return 'doc'
  if (ct.includes('json') || ct.includes('xml') || ct.includes('javascript') || ct.startsWith('text/x-') || ct.includes('typescript')) return 'code'
  if (ct.startsWith('text/')) return 'doc'
  return 'doc'
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return '—'
  if (n < 1024) return `${n} B`
  const u = ['KB', 'MB', 'GB', 'TB']
  let i = -1, v = n
  do { v /= 1024; i++ } while (v >= 1024 && i < u.length - 1)
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${u[i]}`
}

export function percentOf(a: number, b: number): number {
  return b ? Math.round((a / b) * 100) : 0
}

// Calm "time ago" wording for a created_at timestamp (ISO string or epoch).
export function relativeTime(value: string | number | null | undefined): string {
  if (!value) return ''

  const then = typeof value === 'number' ? value : Date.parse(value)
  if (Number.isNaN(then)) return ''

  const secs = Math.max(0, Math.round((Date.now() - then) / 1000))
  const mins = Math.round(secs / 60)
  const hrs = Math.round(mins / 60)
  const days = Math.round(hrs / 24)

  if (secs < 45) return 'just now'
  if (mins < 60) return `${mins} min ago`
  if (hrs < 24) return `${hrs} hr ago`
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 30) return `${Math.round(days / 7)} wk ago`
  if (days < 365) return `${Math.round(days / 30)} mo ago`
  return `${Math.round(days / 365)} yr ago`
}

// Clevver's real asset statuses (app/models/digital_asset_status.rb + purchase.rb):
// created, processing, processed, storing, confirming, stored, caching, cached,
// delivering, delivered. Settled (at-rest) states need no indicator.
const SETTLED = ['stored', 'cached', 'delivered']

// Whether a file is still being processed / written to storage (worth surfacing).
export function isProcessing(status: string): boolean {
  const s = (status || '').toLowerCase()
  return !!s && !SETTLED.includes(s)
}

// Human label for a status, using Clevver's own term (e.g. "Storing", "Caching").
export function statusLabel(status: string): string {
  const s = (status || '').toLowerCase()
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
